import { faCircleCheck } from '@fortawesome/free-solid-svg-icons/faCircleCheck';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons/faCircleNotch';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import camelCase from 'lodash/camelCase';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { useSubscription } from '../../hooks/useSubscription';
import * as AppPropTypes from '../../lib/PropTypes';
import getRatio from '../../lib/getRatio';

import RoundedButton from '../buttons/RoundedButton';
import ToggleButton from '../buttons/ToggleButton';
import ViaButton from '../buttons/ViaButton';
import ToggleIcon from '../icons/ToggleIcon';
import AuthorImage from '../partials/AuthorImage';
import Image from '../partials/Image';
import EditContactModal from '../popups/EditContactModal';
import CardDescription from '../typography/CardDescription';
import CardTitle from '../typography/CardTitle';
import HorizontalCard from './HorizontalCard';

import styles from '../../styles/cards/subscription-horizontal-card.module.css';

const propTypes = {
    handle: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    url: PropTypes.string,
    author: AppPropTypes.author,
    brand: AppPropTypes.taxonomy,
    image: AppPropTypes.image,
    thumbnailWidth: PropTypes.number,
    height: PropTypes.number,
    ratio: PropTypes.string,
    name: PropTypes.string,
    via: PropTypes.arrayOf(PropTypes.string),
    description: PropTypes.string,
    theme: PropTypes.oneOf(['box', 'box-dark', null]),
    source: PropTypes.string,
    checked: PropTypes.bool,
    withButton: PropTypes.bool,
    withToggle: PropTypes.bool,
    withCheckbox: PropTypes.bool,
    onSubscribe: PropTypes.func,
    onUnsubscribe: PropTypes.func,
    onChange: PropTypes.func,
    className: PropTypes.string,
};

const defaultProps = {
    url: null,
    author: null,
    brand: null,
    image: null,
    thumbnailWidth: null,
    height: 110,
    ratio: 'square',
    name: null,
    via: null,
    description: null,
    theme: null,
    source: null,
    checked: false,
    withButton: false,
    withToggle: false,
    withCheckbox: false,
    onSubscribe: null,
    onUnsubscribe: null,
    onChange: null,
    className: null,
};

function SubscriptionHorizontalCard({
    url,
    author,
    type,
    brand,
    handle,
    image,
    thumbnailWidth,
    height,
    ratio,
    name,
    via,
    description,
    theme,
    source,
    checked,
    withButton,
    withToggle,
    withCheckbox,
    onSubscribe,
    onUnsubscribe,
    onChange,
    className,
}) {
    const thumbnailRatio = getRatio(ratio, null);

    const {
        subscribed,
        via: userVia,
        isSubscribing,
        isUnsubscribing,
        subscribe,
        unsubscribe,
        updateVia,
        editContactNeeded,
        closeEditContact,
        onEditContactComplete,
    } = useSubscription({
        subscription: handle,
        source,
        onSubscribe,
        onUnsubscribe,
    });
    const onSubscribeChange = useCallback(
        (newSubscribed) => {
            if (newSubscribed) {
                subscribe();
            } else {
                unsubscribe();
            }
        },
        [subscribe, unsubscribe],
    );

    const isExternal = type === 'external';
    const { name: authorName, description: authorDescription = null } = author || {};
    const { description: brandDescription = null } = brand || {};

    const finalThumbnailWidth = thumbnailWidth || thumbnailRatio * height;
    const finalThumbnailHeight = thumbnailWidth !== null ? thumbnailWidth / thumbnailRatio : height;

    const onCheckboxChange = useCallback(
        (e) => {
            if (onChange !== null) {
                onChange(e.target.checked);
            }
        },
        [onChange],
    );

    return (
        <HorizontalCard
            height={height}
            thumbnailRatio={thumbnailRatio}
            // responsive
            isLabel={withCheckbox}
            thumbnail={
                author !== null ? (
                    <AuthorImage
                        name={authorName}
                        width={finalThumbnailWidth}
                        height={finalThumbnailHeight}
                        media={image}
                        size="small"
                        className={styles.thumbnail}
                        pictureClassName={styles.picture}
                        responsive={thumbnailWidth === null}
                    />
                ) : (
                    <Image
                        width={finalThumbnailWidth}
                        height={finalThumbnailHeight}
                        media={image}
                        size="small"
                        className={styles.thumbnail}
                        pictureClassName={styles.picture}
                        responsive={thumbnailWidth === null}
                        // withoutSize
                    />
                )
            }
            onClick={
                withToggle && !subscribed && !isExternal
                    ? () => onSubscribeChange(!subscribed)
                    : null
            }
            className={classNames([
                styles.container,
                {
                    [styles.isAuthor]: author !== null,
                    [styles.isBrand]: brand !== null,
                    [styles.subscribed]: subscribed && !withCheckbox,
                },
                styles[camelCase(theme)],
                className,
            ])}
            labelClassName={styles.label}
            thumbnailClassName={styles.thumbnail}
        >
            <div className={styles.inner}>
                <CardTitle className={styles.name}>{name}</CardTitle>
                <CardDescription
                    className={styles.description}
                    html={description || authorDescription || brandDescription}
                />
                {via !== null && via.length > 1 && subscribed && !withCheckbox ? (
                    <div className={styles.via}>
                        <strong>Recevoir par :</strong>
                        <ViaButton
                            via={via}
                            value={userVia}
                            onChange={updateVia}
                            className={styles.viaButton}
                        />
                    </div>
                ) : null}
                {withButton ? (
                    <RoundedButton
                        transparent={subscribed}
                        onClick={() => onSubscribeChange(!subscribed)}
                        disabled={isSubscribing || isUnsubscribing}
                        className={classNames([
                            styles.button,
                            {
                                [styles.unsubscribeButton]: subscribed,
                            },
                        ])}
                    >
                        {subscribed ? (
                            <FormattedMessage
                                defaultMessage="Me désabonner"
                                description="Button label"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="M'abonner"
                                description="Button label"
                            />
                        )}
                    </RoundedButton>
                ) : null}
            </div>
            {isExternal ? (
                <span className={styles.actions}>
                    <RoundedButton href={url} external compact>
                        S’abonner
                    </RoundedButton>
                </span>
            ) : null}
            {withToggle && !isExternal ? (
                <span className={styles.actions}>
                    {subscribed ? (
                        <ToggleButton
                            value={subscribed}
                            className={styles.toggle}
                            loading={isUnsubscribing || isSubscribing}
                            onChange={onSubscribeChange}
                        />
                    ) : (
                        <ToggleIcon
                            checked={subscribed}
                            className={styles.toggle}
                            loading={isUnsubscribing || isSubscribing}
                        />
                    )}
                </span>
            ) : null}

            {withCheckbox && !isExternal ? (
                <span className={styles.actions}>
                    <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={checked}
                        onChange={onCheckboxChange}
                    />
                </span>
            ) : null}

            {withButton ? (
                <span className={styles.actions}>
                    {isUnsubscribing || isSubscribing ? (
                        <FontAwesomeIcon
                            icon={faCircleNotch}
                            spin
                            className={classNames([styles.icon, styles.loadingIcon])}
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faCircleCheck}
                            className={classNames([styles.icon, styles.checkIcon])}
                        />
                    )}
                </span>
            ) : null}
            {editContactNeeded !== null ? (
                <EditContactModal
                    type={editContactNeeded}
                    title={
                        editContactNeeded === 'phone' ? (
                            <FormattedMessage
                                defaultMessage="Cet abonnement demande un numéro de téléphone"
                                description="Modal title"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Cet abonnement demande un courriel"
                                description="Modal title"
                            />
                        )
                    }
                    onComplete={onEditContactComplete}
                    onClosed={closeEditContact}
                />
            ) : null}
        </HorizontalCard>
    );
}

SubscriptionHorizontalCard.propTypes = propTypes;
SubscriptionHorizontalCard.defaultProps = defaultProps;

export default SubscriptionHorizontalCard;
